import { ISortBy } from "@pages/Catalog/components/utils";

export const REGEX_EXP_FOR_LENGTH = /^(([0]?[0-5][0-9]|[0-9]):([0-5][0-9])) - (([0]?[0-5][0-9]|[0-9]):([0-5][0-9]))$/;

export const REGEX_EXP_FOR_BPM = /\d{1,3} - \d{1,3} bpm/;

export const FACEBOOK_REGEX =
  /(?:https?:\/\/)?(?:www\.)?(mbasic.facebook|m\.facebook|facebook|fb)\.(com|me)\/(?:(?:\w\.)*#!\/)?(?:pages\/)?(?:[\w\-\.]*\/)*([\w\-\.]*)/gi;

export const INSTAGRAM_REGEX = /^@([a-zA-Z0-9._]+)$/;

export const YOUTUBE_REGEX = /@[\w-]+/gm;

export const TIKTOK_REGEX = /^@([a-zA-Z0-9._]+)$/;

export const BUSINESS_REGEX =
  /^((https?:\/\/)?((([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})|((\d{1,3}\.){3}\d{1,3}))(:\d+)?(\/[a-zA-Z0-9-._~:\/?#[\]@!$&'()*+,;=]*)?)$/;

export const CURRENCY_REGEX = /^([€$])?\d{1,3}([.,]?\d{3})*([.,]\d{1,2})?([€$])?$/;

export const lengthOfId = 24;

export const NUMBER_OF_CODE_DIGITS = 6;

/**
 * sessionStorage key used to pass a one-time message to the LoginDialog.
 * Currently set by the password-change "done" step to surface "Your
 * password has been changed. Please log in with your new password." in
 * the next login. Read + cleared on the LoginDialog Form's first render.
 */
export const LOGIN_NOTICE_KEY = "login-notice";

/**
 * Sibling flag to LOGIN_NOTICE_KEY: when set, SecondRootLayout opens the
 * login dialog automatically on mount (and then clears the flag). Used by
 * any flow that redirects to / after logging the user out and needs the
 * login modal to be visible immediately rather than waiting for the user
 * to click "Log in". Currently set by ChangePasswordModal's "Continue to
 * log in" step.
 */
/**
 * `/?login=<code>` — opens the login modal on load and, for a known code,
 * shows the matching green notice above the form.
 *
 * The URL is the state on purpose. This replaced a sessionStorage handshake
 * that opened the modal and then lost it: the key was consumed on read, the
 * layout re-mounts during navigation, and the re-mounted layout had no way to
 * know the modal should be open. A query param survives any number of
 * re-mounts because the effect simply re-reads it, and it is only removed once
 * the user dismisses the dialog.
 *
 * `?login=1` still works and just opens the modal with no notice.
 */
/**
 * Where AuthGuard should send someone when their session dies while they are
 * on a protected route, instead of the bare "/".
 *
 * Needed because a flow can revoke its OWN session and still have something to
 * say afterwards. The password change is the case in point: the backend kills
 * every session, a background poll then 401s, the interceptor dispatches
 * logout, and AuthGuard redirects off /account — throwing away the "Password
 * changed" step before the user has read it. Setting this makes that
 * involuntary redirect land on the same URL the button would have used, so the
 * outcome is identical whether the user clicks or the session dies first.
 *
 * Read-and-removed by AuthGuard; the destination URL carries the state from
 * there, so nothing depends on this key surviving the navigation.
 */
export const AUTH_REDIRECT_KEY = "as-auth-redirect";

export const LOGIN_NOTICES: Record<string, string> = {
  "password-changed": "Your password has been changed. Please log in again.",
};

export const AUTO_OPEN_LOGIN_KEY = "auto-open-login";
/** Where to send the user after a successful login, when they were bounced off
    a guarded page (e.g. a licence email link opened while logged out). Consumed
    once by the login thunk; falls back to the catalog when absent. */
export const POST_LOGIN_REDIRECT_KEY = "as-post-login-redirect";

export enum ERoutes {
  Home = "/",
  Catalog = "/music",
  CuratedPlaylist = "/c/:slug",
  ContactLicensing = "/contact-licensing/:name",
  Licensing = "/license-request",
  Contact = "/contact",
  FreeUseTerms = "/free-use",
  Submit = "/submit",
  OurArtists = "/our-artists",
  Favorites = "/favorites",
  Profile = "/account",
  Conditions = "/terms-of-use",
  Donation = "/donation-thank-you",
  Newsletter = "/newsletter",
  NewsletterSuccess = "/newsletter-success",
  NewsletterUnsubscribe = "/newsletter-unsubscribe",
  NewsletterPreferences = "/newsletter/preferences",
  Unsubscribe = "/unsubscribe",
  Privacy = "/privacy",
  RefundPolicy = "/refund-policy",
  CookiePolicy = "/cookie-policy",
  LicenceTerms = "/licence-terms",
  // Statutory withdrawal button (§ 13a FAGG). Must stay permanently reachable
  // from every page — it is not an ordinary content route.
  Withdraw = "/withdraw",
  Imprint = "/imprint",
  TransitPage = "/transit-page",
  Pricing = "/pricing",
  CatalogLandingLicensing = "/licensing",
  CatalogLandingFreeBackground = "/free-background-music",
  FreeYoutubeMusic = "/free-youtube-music",
  HowItWorks = "/features", // "Features" page (formerly "How It Works" — /how-it-works)
  Shop = "https://www.sanctum.shop/",
  FaceBook = "https://www.facebook.com/profile.php?id=100090626700207",
  Instagram = "https://www.instagram.com/audio_sanctum",
  Twitter = "https://twitter.com/_audiosanctum",
  Youtube = "https://www.youtube.com/@audio-sanctum",
  YoutubeFreeUsePlaylist = "https://www.youtube.com/watch?v=WPRBx6K02z8&list=PLYhbl-MCBKQeE4Bmh3QKXoWUjKHE1j-0A&ab_channel=AudioSanctum",
  Spotify = "https://open.spotify.com/user/31k5gubg4hn5s6jsbitmbm6yck5q",
  SoundCloud = "https://soundcloud.com/audiosanctum",
  Bandcamp = "https://audiosanctum.bandcamp.com/",
  AppleMusic = "https://music.apple.com/us/artist/audio-sanctum/1660603052",
  DownloadInstruction = "/download-instructions",
  Track = "/song/:id",
  TrackForMapping = "/song",
  Album = "/album/:id",
  AlbumForMapping = "/album",
  MUSIC_CREATOR_PROGRAM = "/music-creator",
  FAQ = "/faq",
  Deals = "/deals",
  SelectedDeal = "/deals/:tag",
  QuickFilter = "/quick",
  SelectedQuickFilter = "/quick/:id",
  CatalogWithTest = "/music?test=true",
  Test = "?test=true",
  OrderSuccess = "/order/success",
  DesignSystem = "/design-system",
  AccountSubscription = "/account/subscription",
  AccountBilling = "/account/billing",
  AccountNotifications = "/account/notifications",
  AccountSettings = "/account/settings",
  AccountDownloads = "/account/downloads",
  AccountVideos = "/account/claims",
  AccountOrders = "/account/orders",
  AccountDataPrivacy = "/account/data-privacy",
  LicenseVerify = "/license/:code",
  AccountDeleted = "/account-deleted",
  /** Not a route anyone navigates to — the catch-all renders NotFound. It exists
      so the not-found screen can carry its own `noindex`, which matters because
      an unknown address is currently answered with 200 and the app shell. */
  NotFound = "/404",
}

enum EContentClassification {
  Art = "Art",
  Cars = "Cars & Vehicles",
  Education = "Educating & Teaching",
  Entertainment = "Entertainment",
  Films = "Film & Animation",
  Gaming = "Gaming",
  Tutorials = "Howto & Tutorials",
  Travel = "Travel & Events",
  Livestreaming = "Livestreaming",
  Meditation = "Meditation & Subliminals",
  Music = "Music",
  News = "News & Politics",
  People = "People & Vlogs",
  Religion = "Religion",
  Science = "Science & Technology",
  Spirituality = "Spirituality",
  Sports = "Sports",
}

enum EUseCases {
  App = "App",
  Clients = "Client Service",
  Commercials = "Commercials & Ads",
  Game = "Game",
  Movie = "Movie, Film & TV",
  Gaming = "Gaming",
  Narrative = "Narrated Content",
  Locations = "Physical Location",
  Podcasts = "Podcast & Radio",
  Events = "Public Event",
  Webinar = "Webinar & Online Course",
}

export const METADATA_OTHER = "Other";

export const CONTENT_CLASSIFICATION = [...Object.values(EContentClassification), METADATA_OTHER];
export const USE_CASES = [...Object.values(EUseCases), METADATA_OTHER];

export enum EMetaDataType {
  UseCases = "USE_CASES",
  ClassifyContent = "CLASSIFY_CONTENT",
}

export enum SoundExamplesPage {
  Home = "Home",
  FreeUseLanding = "Free use landing",
  Licensing = "Licensing",
}

export enum AllFilter {
  ALL = "All",
}

export const sortValues = (t: (key: string) => string): ISortBy[] => [
  {
    // Default sort (first entry = the pre-selected default). Trending blends
    // real engagement velocity with a new-track boost; until that data builds,
    // most scores tie near 0 so the order is loose — revisit weighting once
    // cart/heart/download/claim signal accumulates.
    value: "trending",
    title: t("sortBy.trending"),
    sortValue: { trending: "DESC" },
  },
  {
    value: "mostViews",
    title: t("sortBy.mostViews"),
    sortValue: { views: "DESC" },
  },
  {
    value: "mostDownloads",
    title: t("sortBy.mostDownloads"),
    sortValue: { downloads: "DESC" },
  },
  {
    value: "recentlyAdded",
    title: t("sortBy.recentlyAdded"),
    sortValue: { createdAt: "DESC" },
  },
  {
    value: "lengthShortest",
    title: t("sortBy.lengthShortest"),
    sortValue: { "summary.length": "ASC" },
  },
  {
    value: "lengthLongest",
    title: t("sortBy.lengthLongest"),
    sortValue: { "summary.length": "DESC" },
  },
  // {
  //   value: "releaseNewest",
  //   title: t("sortBy.releaseNewest"),
  //   sortValue: { releaseDate: "DESC" },
  // },
  // {
  //   value: "releaseOldest",
  //   title: t("sortBy.releaseOldest"),
  //   sortValue: { releaseDate: "ASC" },
  // },
  {
    value: "tempoFastest",
    title: t("sortBy.tempoFastest"),
    sortValue: { "summary.bpm": "DESC" },
  },
  {
    value: "tempoSlowest",
    title: t("sortBy.tempoSlowest"),
    sortValue: { "summary.bpm": "ASC" },
  },
  {
    // Seed is injected at request time from sessionStorage (getShuffleSeed) so
    // the order is stable within a session — the placeholder here is replaced
    // before the request goes out.
    value: "random",
    title: t("sortBy.random"),
    sortValue: { random: "" },
  },
];

/** Sort options actually shown in the `/music` dropdown. `random` (Shuffle) is
    intentionally **hidden** for now — it only made sense as the default, and as a
    manual option a "randomize" pick has little value on a licensing catalog. The
    sort itself stays intact (logic, session seed, `?sort=random` still work), so
    bringing it back is just removing it from `HIDDEN_SORTS`. */
const HIDDEN_SORTS = ["random"];
export const visibleSortValues = (t: (key: string) => string): ISortBy[] =>
  sortValues(t).filter((s) => !HIDDEN_SORTS.includes(s.value));

export const sortValuesQueryMapper = {
  recentlyAdded: { createdAt: "DESC" },
  trending: { trending: "DESC" },
  mostDownloads: { downloads: "DESC" },
  mostViews: { views: "DESC" },
  lengthShortest: { "summary.length": "ASC" },
  lengthLongest: { "summary.length": "DESC" },
  releaseNewest: { createdAt: "DESC" },
  releaseOldest: { createdAt: "ASC" },
  tempoFastest: { "summary.bpm": "DESC" },
  tempoSlowest: { "summary.bpm": "ASC" },
  random: { random: "" },
};

export enum ComposersName {
  PATRICK_LENK = "Patrick Lenk",
  SIMON_DAUM = "Simon Daum",
}

